import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Chip, IconButton, Tooltip, TextField } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { fetchAgentActivationCode, generateAgentActivationCode } from '../../../api/agentApi';
import { useAgentAuthorization } from '../../../hooks/useAgentAuthorization';
import { getFirestore, collection, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../libs/contexts/TenantContext';

/**
 * Agent Edit Modal
 *
 * Modal for editing agent configuration.
 * IMPORTANT: Field names MUST match the agent.js config exactly.
 * These fields are directly mapped to environment variables in the agent.
 */
const AgentEditModal = ({ agent, onSave, onClose, tenants = [] }) => {
  const { userRole, userTenant } = useAgentAuthorization();
  const { userData } = useAuth();
  const { availableTenants, selectedTenant } = useTenant();
  const isAdmin = userRole === 'admin' || userRole === 'superadmin';
  const isTenantAdmin = userRole === 'tenant_admin';
  const isSiteScoped = ['site_admin', 'operator', 'viewer'].includes(userRole);
  const canManageActivation =
    userRole === 'admin' || userRole === 'superadmin' || userRole === 'tenant_admin';
  
  // Phase 1C: Sites state
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    agentId: '',
    agentName: '',
    agentTenant: '',
    siteId: '',
    agentSipId: '',
    agentRestId: '',
    heartbeatInterval: 30000,
    features: [],
  });

  const [activation, setActivation] = useState({
    code: '',
    status: '',
    createdAt: null,
    usedAt: null,
    deviceInfo: null,
    loading: false,
    generating: false,
  });

  const formatTimestamp = value => {
    if (!value) return '';
    try {
      const date =
        typeof value === 'string'
          ? new Date(value)
          : value && typeof value === 'object' && value._seconds
          ? new Date(value._seconds * 1000)
          : new Date(value);
      if (!date || Number.isNaN(date.getTime())) return '';
      return date.toLocaleString();
    } catch (e) {
      return '';
    }
  };

  const handleCopyActivationCode = () => {
    if (!activation.code) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(activation.code).catch(() => {});
    }
  };

  const handleGenerateActivationCode = async () => {
    if (!agent || !agent.agentId) return;
    setActivation(prev => ({ ...prev, generating: true }));
    try {
      const data = await generateAgentActivationCode(agent.agentId);
      setActivation(prev => ({
        ...prev,
        code: data.activation_code || '',
        status: data.activation_status || 'pending',
        createdAt: data.activation_created_at || prev.createdAt,
        usedAt: data.activation_used_at || null,
        deviceInfo: data.activation_device_info || null,
        generating: false,
        loading: false,
      }));
    } catch (e) {
      setActivation(prev => ({ ...prev, generating: false }));
    }
  };

  // Helper to normalize features into an array of strings
  const normalizeFeatures = raw => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') return [raw];
    if (typeof raw === 'object') {
      // Common pattern: { sip: true, rest: false }
      return Object.entries(raw)
        .filter(([, enabled]) => !!enabled)
        .map(([key]) => key);
    }
    return [];
  };

  // Phase 1C Part 2: Sites subscription
  useEffect(() => {
    const tenantId = userTenant || userData?.tenantId || agent?.tenantId || agent?.agentTenant;
    if (!tenantId) {
      setSitesLoading(false);
      return;
    }
    
    const db = getFirestore();
    const sitesRef = collection(db, 'tenants', tenantId, 'sites');
    
    console.log('[AgentEditModal] Setting up sites subscription for tenant:', tenantId);
    
    const unsubscribe = onSnapshot(
      sitesRef,
      (snapshot) => {
        const sitesArray = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || doc.id,
        }));
        console.log(`[AgentEditModal] Sites loaded: ${sitesArray.length} sites`, sitesArray);
        setSites(sitesArray);
        setSitesLoading(false);
      },
      (error) => {
        console.error('[AgentEditModal] Error loading sites:', error);
        setSitesLoading(false);
      }
    );
    
    return () => unsubscribe();
  }, [userTenant, userData, agent]);

  // Initialize form with agent data when available
  useEffect(() => {
    if (agent) {
      const agentSiteId = agent.siteId || agent.site_id || '';
      setFormData({
        ...agent,
        siteId: agentSiteId,
        // Ensure features is always a normalized array
        features: normalizeFeatures(agent.features),
      });
    }
  }, [agent]);

  useEffect(() => {
    if (!agent || !agent.agentId) {
      setActivation(prev => ({
        ...prev,
        code: '',
        status: '',
        createdAt: null,
        usedAt: null,
        deviceInfo: null,
        loading: false,
        generating: false,
      }));
      return;
    }
    let isCancelled = false;
    setActivation(prev => ({ ...prev, loading: true }));
    fetchAgentActivationCode(agent.agentId)
      .then(data => {
        if (isCancelled) return;
        setActivation({
          code: data.activation_code || '',
          status:
            data.activation_status || (data.activation_code ? 'pending' : 'none'),
          createdAt: data.activation_created_at || null,
          usedAt: data.activation_used_at || null,
          deviceInfo: data.activation_device_info || null,
          loading: false,
          generating: false,
        });
      })
      .catch(() => {
        if (isCancelled) return;
        setActivation(prev => ({ ...prev, loading: false }));
      });
    return () => {
      isCancelled = true;
    };
  }, [agent]);

  // Handle form input changes
  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle features multiselect
  const handleFeatureToggle = feature => {
    const currentFeatures = Array.isArray(formData.features) ? formData.features : normalizeFeatures(formData.features);

    if (currentFeatures.includes(feature)) {
      setFormData({
        ...formData,
        features: currentFeatures.filter(f => f !== feature),
      });
    } else {
      setFormData({
        ...formData,
        features: [...currentFeatures, feature],
      });
    }
  };

  // Handle form submission
  const handleSubmit = e => {
    e.preventDefault();
    onSave(formData);
  };

  const availableFeatures = ['sip', 'rest', 'gpio', 'bluetooth', 'zigbee'];

  const tenantChoices = (Array.isArray(tenants) && tenants.length > 0)
    ? tenants
    : (Array.isArray(availableTenants) ? availableTenants : []);
  const tenantName = (
    tenantChoices.find((t) => t.id === formData.agentTenant)?.name
    || (selectedTenant?.id === formData.agentTenant ? selectedTenant?.name : null)
    || ''
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-lg p-4 w-full max-w-2xl"
        style={{ maxHeight: '90vh', overflowY: 'auto' }}
      >
        <h2 className="text-xl font-bold mb-3">Edit Agent: {agent?.agentName}</h2>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Agent ID</label>
              <input
                type="text"
                name="agentId"
                value={formData.agentId}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name</label>
              <input
                type="text"
                name="agentName"
                value={formData.agentName}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Tenant</label>
              {isAdmin && tenantChoices.length > 0 ? (
                <select
                  name="agentTenant"
                  value={formData.agentTenant || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">— No Tenant —</option>
                  {tenantChoices.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name || t.id}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={tenantName || formData.agentTenant || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                />
              )}
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Site</label>
              {isSiteScoped ? (
                <input
                  type="text"
                  value={sites.find(s => s.id === formData.siteId)?.name || formData.siteId || 'Loading...'}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                />
              ) : (
                <select
                  name="siteId"
                  value={formData.siteId || ''}
                  onChange={handleChange}
                  disabled={sitesLoading || sites.length === 0 || (!isTenantAdmin && !isAdmin)}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md ${
                    (!isTenantAdmin && !isAdmin) || sitesLoading || sites.length === 0
                      ? 'bg-gray-100 cursor-not-allowed text-gray-600'
                      : ''
                  }`}
                >
                  <option value="">
                    {sitesLoading ? 'Loading sites...' : sites.length === 0 ? 'No sites available' : '— No Site —'}
                  </option>
                  {sites.map(site => (
                    <option key={site.id} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Features */}
            {false && (
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Features</label>
              <div className="flex flex-wrap gap-2 p-2 border border-gray-300 rounded-md">
                {availableFeatures.map(feature => (
                  <div key={feature} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`feature-${feature}`}
                      checked={Array.isArray(formData.features) ? formData.features.includes(feature) : normalizeFeatures(formData.features).includes(feature)}
                      onChange={() => handleFeatureToggle(feature)}
                      className="mr-1"
                    />
                    <label htmlFor={`feature-${feature}`} className="text-sm">
                      {feature}
                    </label>
                  </div>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Maps to AGENT_FEATURES (JSON array) in .env
              </p>
            </div>
            )}
          </div>

          <Box sx={{ mt: 3, p: 2, borderRadius: 1, border: '1px solid', borderColor: 'grey.300', bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1">Activation</Typography>
              {canManageActivation && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={handleGenerateActivationCode}
                  disabled={activation.generating || activation.loading}
                >
                  {activation.code ? 'Regenerate activation code' : 'Generate activation code'}
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                Status:
              </Typography>
              <Chip
                size="small"
                label={
                  activation.status === 'activated'
                    ? 'Activated'
                    : activation.status === 'pending'
                    ? 'Pending activation'
                    : activation.status === 'none'
                    ? 'No code'
                    : activation.status || 'Unknown'
                }
                color={
                  activation.status === 'activated'
                    ? 'success'
                    : activation.status === 'pending'
                    ? 'warning'
                    : 'default'
                }
                variant="outlined"
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TextField
                label="Activation code"
                value={activation.code || ''}
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
              />
              <Tooltip title="Copy activation code">
                <span>
                  <IconButton
                    size="small"
                    sx={{ ml: 1 }}
                    onClick={handleCopyActivationCode}
                    disabled={!activation.code}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: activation.deviceInfo ? 1 : 0 }}>
              <Typography variant="caption">
                Created: {activation.createdAt ? formatTimestamp(activation.createdAt) : '—'}
              </Typography>
              <Typography variant="caption">
                Last used: {activation.usedAt ? formatTimestamp(activation.usedAt) : '—'}
              </Typography>
            </Box>
            {activation.deviceInfo && (
              <Typography variant="caption">
                Last device: {activation.deviceInfo.hostname || 'Unknown host'}
                {activation.deviceInfo.os ? ` (${activation.deviceInfo.os})` : ''}
              </Typography>
            )}
            {!canManageActivation && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                Activation codes are managed by your system administrator.
              </Typography>
            )}
          </Box>

          <div
            className="mt-6 flex justify-end space-x-3"
            style={{
              position: 'sticky',
              bottom: 0,
              background: '#fff',
              paddingTop: 16,
              paddingBottom: 8,
              borderTop: '1px solid #e5e7eb',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AgentEditModal;
